import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';

/**
 * A confirmation before something that cannot be taken back.
 *
 * The confirm button says **what it will do** — "Cancel the job", "Deactivate
 * Sam Cleaner" — not "Confirm". Two reasons: someone who arrived at this dialog
 * by mistake needs to be able to tell from the button alone, and a dialog whose
 * buttons read "Cancel / Confirm" on a screen about cancelling a job is a trap.
 *
 * Radix handles the focus trap, the Escape key and the `aria-modal` wiring, so
 * they are correct rather than approximated.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  /** "Deactivate Sam Cleaner". Never "Confirm", never "OK". */
  confirmLabel,
  cancelLabel = 'Keep as it is',
  destructive = false,
  isPending = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  isPending?: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <Dialog.Title className="text-h2 text-ink-900">{title}</Dialog.Title>
          <Dialog.Description className="mt-2 text-body text-ink-700">
            {description}
          </Dialog.Description>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Dialog.Close asChild>
              <Button variant="outline" disabled={isPending}>
                {cancelLabel}
              </Button>
            </Dialog.Close>
            <Button
              variant={destructive ? 'danger' : 'primary'}
              disabled={isPending}
              onClick={onConfirm}
            >
              {isPending ? 'Working…' : confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
